import { useState, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Container,
  Stack,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ChromePicker } from 'react-color';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import Swal from 'sweetalert2';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

const CreateTheme = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  
  // Get username from localStorage
  const username = localStorage.getItem('username') || 'Customer';

  const [themeData, setThemeData] = useState({
    name: '',
    type: '',
    themeImage: '',
    isDefault: false,
    headerColor: '#dc2626',
    backgroundColor: '#f8f9fa',
    textColor: '#333333',
    linkColor: '#dc2626',
    greeting: `Hi ${username},`,
    title: "Let's design an Email Layout!",
    content: "You're going to need some text, probably with a link for the customer to click on",
    footerText: 'Best regards,\nYour Team'
  });

  const [showColorPicker, setShowColorPicker] = useState({
    header: false,
    background: false,
    text: false,
    link: false
  });

  // Remove theme types array since we're using text input now

  const handleInputChange = (field, value) => {
    setThemeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColorChange = (colorType, color) => {
    setThemeData(prev => ({
      ...prev,
      [`${colorType}Color`]: color.hex
    }));
  };

  const toggleColorPicker = (colorType) => {
    setShowColorPicker(prev => ({
      ...prev,
      [colorType]: !prev[colorType]
    }));
  };

  const handleSave = async () => {
    if (!themeData.name || !themeData.type) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    try {
      const response = await postRequest(
        'private/email-themes/create',
        themeData,
        authCtx.token
      );
      
      if (response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Email theme created successfully!'
        }).then(() => {
          navigate('/communication/email-theming');
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.message || 'Failed to create email theme'
      });
    }
  };

  const handleCancel = () => {
    navigate('/communication/email-theming');
  };

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Email Theming', path: '/communication/email-theming' },
            { label: 'Create Theme', path: '' }
          ]}
        />

        {/* Header */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <Typography variant="h4" fontWeight="400" color="text.primary">
            Create New Email Theme
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} lg={6}>
            <Card
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff',
                height: '100%'
              }}
            >
              {/* Basic Information Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{ mb: 0.5, color: '#1f2937' }}
                >
                  Basic Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Set up the basic details for your email theme
                </Typography>

                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Theme Name"
                    value={themeData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., My Custom Theme"
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff'
                        }
                      }
                    }}
                  />

                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={themeData.isDefault}
                          onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                          sx={{
                            color: '#7c3aed',
                            '&.Mui-checked': {
                              color: '#7c3aed',
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="500">
                            Is Default Email Theme
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            This theme will be used as the default template for emails
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Theme Type"
                    value={themeData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    placeholder="e.g., Corporate, Modern, Legal, etc."
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff'
                        }
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Theme Image URL (Optional)"
                    value={themeData.themeImage}
                    onChange={(e) => handleInputChange('themeImage', e.target.value)}
                    placeholder="https://example.com/theme-image.jpg"
                    helperText="Enter the URL of the theme preview image. This will be displayed instead of the theme type text."
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff'
                        }
                      }
                    }}
                  />

                  {themeData.themeImage && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: '#fafafa',
                        borderRadius: 2,
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <Typography variant="body2" fontWeight="500" sx={{ mb: 1, color: '#6b7280' }}>
                        Image Preview:
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 2,
                          backgroundColor: '#ffffff',
                          borderRadius: 2,
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <img
                          src={themeData.themeImage}
                          alt="Theme preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '150px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<p style="color: #dc2626; font-size: 14px;">Failed to load image. Please check the URL.</p>';
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Color Configuration Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{ mb: 0.5, color: '#1f2937' }}
                >
                  Color Palette
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Customize the colors for your email theme
                </Typography>

                <Grid container spacing={3}>
                  {/* Header Color */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography variant="body1" fontWeight="500" sx={{ mb: 2 }}>
                        Header Color
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            backgroundColor: themeData.headerColor,
                            border: '3px solid #e5e7eb',
                            borderRadius: 3,
                            cursor: 'pointer',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 8px 15px -3px rgba(0, 0, 0, 0.1)'
                            }
                          }}
                          onClick={() => toggleColorPicker('header')}
                        />
                        <TextField
                          value={themeData.headerColor}
                          onChange={(e) => handleInputChange('headerColor', e.target.value)}
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: '#fafafa'
                            }
                          }}
                        />
                      </Box>
                      {showColorPicker.header && (
                        <Box sx={{ position: 'absolute', zIndex: 10, mt: 2 }}>
                          <Box
                            sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                            onClick={() => toggleColorPicker('header')}
                          />
                          <Box sx={{ 
                            borderRadius: 2, 
                            overflow: 'hidden',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                          }}>
                            <ChromePicker
                              color={themeData.headerColor}
                              onChange={(color) => handleColorChange('header', color)}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Background Color */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography variant="body1" fontWeight="500" sx={{ mb: 2 }}>
                        Background Color
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            backgroundColor: themeData.backgroundColor,
                            border: '3px solid #e5e7eb',
                            borderRadius: 3,
                            cursor: 'pointer',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 8px 15px -3px rgba(0, 0, 0, 0.1)'
                            }
                          }}
                          onClick={() => toggleColorPicker('background')}
                        />
                        <TextField
                          value={themeData.backgroundColor}
                          onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: '#fafafa'
                            }
                          }}
                        />
                      </Box>
                      {showColorPicker.background && (
                        <Box sx={{ position: 'absolute', zIndex: 10, mt: 2 }}>
                          <Box
                            sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                            onClick={() => toggleColorPicker('background')}
                          />
                          <Box sx={{ 
                            borderRadius: 2, 
                            overflow: 'hidden',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                          }}>
                            <ChromePicker
                              color={themeData.backgroundColor}
                              onChange={(color) => handleColorChange('background', color)}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Text Color */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography variant="body1" fontWeight="500" sx={{ mb: 2 }}>
                        Text Color
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            backgroundColor: themeData.textColor,
                            border: '3px solid #e5e7eb',
                            borderRadius: 3,
                            cursor: 'pointer',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 8px 15px -3px rgba(0, 0, 0, 0.1)'
                            }
                          }}
                          onClick={() => toggleColorPicker('text')}
                        />
                        <TextField
                          value={themeData.textColor}
                          onChange={(e) => handleInputChange('textColor', e.target.value)}
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: '#fafafa'
                            }
                          }}
                        />
                      </Box>
                      {showColorPicker.text && (
                        <Box sx={{ position: 'absolute', zIndex: 10, mt: 2 }}>
                          <Box
                            sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                            onClick={() => toggleColorPicker('text')}
                          />
                          <Box sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                          }}>
                            <ChromePicker
                              color={themeData.textColor}
                              onChange={(color) => handleColorChange('text', color)}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Link Color */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography variant="body1" fontWeight="500" sx={{ mb: 2 }}>
                        Link Color
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            backgroundColor: themeData.linkColor,
                            border: '3px solid #e5e7eb',
                            borderRadius: 3,
                            cursor: 'pointer',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 8px 15px -3px rgba(0, 0, 0, 0.1)'
                            }
                          }}
                          onClick={() => toggleColorPicker('link')}
                        />
                        <TextField
                          value={themeData.linkColor}
                          onChange={(e) => handleInputChange('linkColor', e.target.value)}
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: '#fafafa'
                            }
                          }}
                        />
                      </Box>
                      {showColorPicker.link && (
                        <Box sx={{ position: 'absolute', zIndex: 10, mt: 2 }}>
                          <Box
                            sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                            onClick={() => toggleColorPicker('link')}
                          />
                          <Box sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                          }}>
                            <ChromePicker
                              color={themeData.linkColor}
                              onChange={(color) => handleColorChange('link', color)}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Content Configuration Section */}
              <Box>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{ mb: 0.5, color: '#1f2937' }}
                >
                  Email Content
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Define the text content and structure of your email template
                </Typography>

                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Greeting Message"
                    value={themeData.greeting}
                    onChange={(e) => handleInputChange('greeting', e.target.value)}
                    placeholder={`Hi ${username},`}
                    helperText="This greeting will be personalized for each customer"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff'
                        }
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Email Title/Subject"
                    value={themeData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Main headline for your email"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff'
                        }
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Main Content"
                    value={themeData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    multiline
                    rows={4}
                    placeholder="Write your main email content here..."
                    helperText="This is the main body of your email message"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff'
                        }
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Footer Text"
                    value={themeData.footerText}
                    onChange={(e) => handleInputChange('footerText', e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Best regards,&#10;Your Team Name"
                    helperText="Optional footer content like contact information or signatures"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff'
                        }
                      }
                    }}
                  />
                </Stack>
              </Box>
            </Card>
          </Grid>

          {/* Preview Section */}
          <Grid item xs={12} lg={6}>
            <Card
              elevation={0}
              sx={{
                position: 'sticky',
                top: 20,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                height: 'fit-content',
                maxHeight: 'calc(100vh - 100px)'
              }}
            >
              <Box sx={{
                p: 2.5,
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#fafafa'
              }}>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#1f2937'
                  }}
                >
                  <PreviewIcon sx={{ color: '#7c3aed', fontSize: 22 }} /> Live Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                  See how your email theme will look
                </Typography>
              </Box>

              <CardContent sx={{ p: 0, height: 550, overflow: 'auto' }}>
                <Box
                  sx={{
                    height: '100%',
                    backgroundColor: themeData.backgroundColor,
                    p: 3,
                    position: 'relative'
                  }}
                >
                  {/* Mock Email Container */}
                  <Box
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: 2,
                      p: 3,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    {/* Email Header */}
                    {themeData.themeImage ? (
                      <Box
                        sx={{
                          mb: 3,
                          textAlign: 'center',
                          display: 'flex',
                          justifyContent: 'center',
                          borderBottom: `2px solid ${themeData.headerColor}`,
                          pb: 2
                        }}
                      >
                        <img
                          src={themeData.themeImage}
                          alt={themeData.name}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '120px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="h4"
                        sx={{
                          color: themeData.headerColor,
                          fontWeight: 'bold',
                          mb: 3,
                          textAlign: 'center',
                          borderBottom: `2px solid ${themeData.headerColor}`,
                          pb: 2
                        }}
                      >
                        {themeData.type || 'Theme Preview'}
                      </Typography>
                    )}

                    {/* Email Content */}
                    <Typography 
                      variant="h6" 
                      sx={{ mb: 3, color: themeData.textColor }}
                    >
                      {themeData.greeting || `Hi ${username},`}
                    </Typography>

                    <Typography
                      variant="h5"
                      sx={{
                        color: themeData.headerColor,
                        fontWeight: '600',
                        mb: 3
                      }}
                    >
                      {themeData.title || "Let's design an Email Layout!"}
                    </Typography>

                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mb: 4, 
                        color: themeData.textColor, 
                        whiteSpace: 'pre-line',
                        lineHeight: 1.6
                      }}
                    >
                      {themeData.content || "You're going to need some text, probably with a link for the customer to click on"}{' '}
                      <Typography
                        component="span"
                        sx={{
                          color: themeData.linkColor,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        example link
                      </Typography>
                    </Typography>

                    {/* Email Footer */}
                    <Box
                      sx={{
                        mt: 4,
                        pt: 3,
                        borderTop: '1px solid #e5e7eb'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: themeData.textColor,
                          whiteSpace: 'pre-line',
                          fontStyle: 'italic'
                        }}
                      >
                        {themeData.footerText || 'Best regards,\nYour Team'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{
          mt: 4,
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#f8f9fa',
          py: 2,
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            sx={{
              borderColor: '#6b7280',
              color: '#6b7280',
              px: 3,
              py: 1,
              borderRadius: 2,
              '&:hover': {
                borderColor: '#4b5563',
                color: '#4b5563',
                backgroundColor: '#f9fafb'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{
              backgroundColor: '#7c3aed',
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)',
              '&:hover': {
                backgroundColor: '#6d28d9',
                boxShadow: '0 4px 6px rgba(124, 58, 237, 0.3)'
              }
            }}
          >
            Create Theme
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CreateTheme;